import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Button, Form, Label, Tab, TabPane } from 'semantic-ui-react';
import TradingSlotSettingsService from '~/api/services/tradingSlotSettings';
import Dialog from '~/components/Dialog';
import FileUpload from '~/components/FileUpload';
import { FormFields } from '~/components/FormFields';
import InfoPopup from '~/components/InfoPopup';
import { b64toBlob, handleErrors } from '~/utils/helpers';
import { CardWrapper, StyledDeleteButton, TabPaneLangugeStyled } from './style';

interface TradingRoomProps {
  languages: any[]
  watch: any
  tradingSlotTerms: any
  control: any
  getValues: (payload?: string | string[]) => any
  setValue: (name: string, value: unknown, config?: any) => void
  texts: any
  tabIndex: number
}

const TradingRoom: React.FC<TradingRoomProps> = (props) => {
  const {
    languages,
    watch,
    tradingSlotTerms,
    control,
    getValues,
    setValue,
    texts } = props
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0);
  const isTradingRoomTrue = watch('tradingRoom');
  const [fileSource, setFileSource] = useState(null)

  useEffect(() => {
    if (props.tabIndex >= 0) {
      setActiveIndex(props.tabIndex)
    }
  }, [props.tabIndex])

  function addTradingSlotTermFile() {
    const tstfl = languages.map((lang, index) => {
      return {
        id: lang.id,
        title: '',
        file: '',
        visible: 1,
        originalFilename: ''
      }
    })
    tradingSlotTerms.append({ languages: tstfl });
  }

  function removeTradingSlotTermFile(index) {
    tradingSlotTerms.remove(index);
  }

  async function handleDownloadPdf(fileName, fileSrc) {
    setIsBusy(true)
    try {
      if (!fileSrc) {
        const response = await TradingSlotSettingsService.getFileTradingSlotSettings({ fileName: fileName })
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl)
      } else {
        const type = fileSrc.split(';')[0].split('data:')[1]
        const blob = b64toBlob(fileSrc, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function getFileName(slotIndex, languageIndex) {
    // @ts-ignore
    return tradingSlotTerms.fields[slotIndex].languages[languageIndex]?.originalFilename
  }

  function renderTradingRoomLanguagePane(language, index) {
    return (
      <TabPaneLangugeStyled
        active={index === activeIndex}
        key={index}
      >
        {texts.fields.map((text, textIndex) => {
          const required = isTradingRoomTrue && (textIndex > 0 ?
            (getValues(`texts[1].languages[${index}].translation`) !== '') : (getValues(`texts[0].languages[${index}].translation`) !== ''));
          return (
            <FormFields.TextArea
              key={text.id}
              // @ts-ignore: semantic type error
              label={text.label}
              // @ts-ignore: semantic type error
              placeholder={text.label}
              rules={{
                required: isTradingRoomTrue ? t('Please enter text for Submit offer') : null
              }}
              control={control}
              name={`texts[${textIndex}].languages[${index}].translation`}
            />
          )
        })}
        <Button
          onClick={() => addTradingSlotTermFile()}
          primary
          size='mini'
          type={'button'}
          labelPosition='right'
          content={t('Add new document')}
          icon='plus'
        />
        {tradingSlotTerms?.fields.map((term, slotIndex) => {
          const langArray = tradingSlotTerms?.fields[slotIndex]?.languages
          const indexLang = langArray?.findIndex(field => field.id === language.id)
          const fieldName = `tradingSlotTerms[${slotIndex}]`;
          return (
            <CardWrapper
              key={term.id}
            >
              <FormFields.Checkbox
                name={`${fieldName}.languages[${indexLang}].visible`}
                label={t('Visible')}
                checkboxValue={1}
                defaultValue={0}
                control={control}
              />
              <FormFields.Input
                label={t('Title')}
                placeholder={t('Title')}
                control={control}
                name={`${fieldName}.languages[${indexLang}].title`}
                rules={{ required: { value: isTradingRoomTrue, message: t('Please enter file title') } }}
              />
              <div>
                {Boolean(getFileName(slotIndex, indexLang)) && <div style={{ margin: '1em', marginLeft: 0 }}>
                  <Label
                    onClick={() => handleDownloadPdf(getValues(`${fieldName}.languages[${indexLang}].filename`), getValues(`${fieldName}.languages[${indexLang}].file`))}
                    color='blue'
                    as='a'
                    content={getFileName(slotIndex, indexLang)}
                    icon='file'
                  />
                </div>
                }
                <Form.Group style={{ alignItems: 'baseline' }}>
                  <FormFields.External
                    name={`${fieldName}.languages[${indexLang}].file`}
                    control={control}
                    keyName={'filename'}
                    onChange={(value, name) => {
                      setIsBusy(true)
                      // setValue(`${fieldName}.languages[${indexLang}]`, {})
                      setValue(`${fieldName}.languages[${indexLang}].file`, value)
                      setValue(`${fieldName}.languages[${indexLang}].originalFilename`, name)
                      setIsBusy(false)
                    }}
                  >
                    <FileUpload
                      type='button'
                      encodeFiles
                      accept={{
                        'application/pdf': ['.pdf']
                      }}
                      sizeLimitInMB={10}
                    />
                  </FormFields.External>
                </Form.Group>
                <StyledDeleteButton
                  onClick={() => removeTradingSlotTermFile(slotIndex)}
                  size={'small'}
                  basic
                  color='red'
                  type={'button'}
                  icon='trash' />
              </div>
            </CardWrapper>
          )
        })}
      </TabPaneLangugeStyled>
    )
  }

  function getLabel(label, infoMessage) {
    return <label style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <div>{label}</div>
      <InfoPopup
        content={infoMessage}
        styleIcon={{ fontSize: '1.2em', cursor: 'pointer' }}
      />
    </label>
  }

  function renderTradingRoomContent() {
    const panes = languages?.map((language, index) => {
      return {
        menuItem: language?.label || null,
        component: renderTradingRoomLanguagePane(language, index)
      }
    })

    return (<>
      <FormFields.Checkbox
        control={control}
        checkboxValue={1}
        defaultValue={0}
        name={'tradingRoom'}
        label={getLabel(
          t('Enter trading room?'),
          t('Do you want to use the "Enter trading room" function? This forces the farmer user to take note of and confirm the notes, contracts, agreements, \n\
          etc. when opening a new trading slot for the first time and when confirming offered amounts. \n\
           This process is also documented in the action log.')
        )}
      />
      <FormFields.Checkbox
        control={control}
        checkboxValue={1}
        defaultValue={0}
        label={getLabel(
          t('Sending documents as e-mail attachments?'),
          t('When this function is activated, the respective contract documents from the trading room are attached to the farmer as a \n\
          PDF when submitting offer quotas and when confirming fixed price quantities.')
        )}
        name={'sendTradingRoomFiles'}
      />
      <Tab menu={{ secondary: true, pointing: true }}
        activeIndex={activeIndex}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
        panes={
          panes.map((pane, index) => {
            return {
              menuItem: pane.menuItem,
              pane: <TabPane
                //eslint-disable-next-line
                children={pane.component}
                active={index === activeIndex}
                style={{ border: 'none' }}
                key={pane.menuItem}
              />
            }
          })} />
    </>)
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource) || isBusy}
      handleClose={() => setFileSource(null)}
      textContent={fileSource && <iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      isLoading={isBusy}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  return <>
    {renderTradingRoomContent()}
    {(fileSource || isBusy) && renderModalPreviewFile()}
  </>
}
export default TradingRoom